import { Business } from '../business';
import { ItemMaster } from '../item/item-master/item-master';
import { LocationPath } from './location';
import { UserLite } from '../user-master/user-lite';
import { AuditTrail } from '@card-software/nexus-models';
import { EntityKeyByReference } from '@card-software/nexus-utilities';

// ── Requisition ──

export interface Requisition extends AuditTrail {
  id?: string;
  type: number;
  requisitionNumber?: string;
  initiatorNumber?: string;
  lotItems: LotItem[];
  initiator: EntityKeyByReference;
  requisitionEntries: RequisitionEntry[];
  status: number;
  requester: EntityKeyByReference;
  plannedIssuer: EntityKeyByReference;
  notes?: RequisitionNote[];
}

export interface RequisitionEntry {
  itemCode: string;
  materialForItemCode: string;
  providerReference: string;
  providerLotNumber: string;
  unitOfMeasure: number;
  requiredQuantityInUnitOfMeasure: number;
  manufacturer?: Business;
}

export enum RequisitionStatus {
  Requested,
  Processing,
  InRoute,
  Received,
  Rejected,
  Voided,
}

export interface RequisitionSearchCommand {
  requisitionNumber?: string;
  initiator?: EntityKeyByReference;
  statuses?: number[];
  types?: number[];
}

// ── LotItem ──

export interface LotItem {
  createdBy?: string;
  createdDate?: Date;
  currentLocationEntityKey?: EntityKeyByReference;
  customer?: Customer;
  customerOrderNumber?: string;
  customerReference?: string | null;
  dateReceived?: Date;
  exactLocationReference?: string;
  expiryDate?: Date; // PBI 4543: now a DateTime (was YearWeek)
  fieldToDisplay?: string;
  grossQuantity: number;
  id?: string;
  inbound: boolean;
  invoice?: Invoice;
  invoiceReference: string;
  isActive: boolean;
  item?: ItemMaster;
  itemCode: string;
  itemManufacturedDate?: YearWeek;
  local?: boolean;
  consumptionEntries: ConsumptionEntry[];
  locationReference?: string;
  modifiedBy?: string;
  modifiedDate?: Date;
  netQuantity: number;
  currentQuantity?: number;
  notes?: RequisitionNote[];
  number?: string;
  originalGrossQuantity?: number;
  originalNetQuantity?: number;
  packagingType: number;
  provider?: Provider;
  providerLabelNumber: string;
  providerLotId: string;
  providerReference: string;
  manufacturerReference?: string;
  manufacturer?: Business;
  received: boolean;
  UOM?: number;
  warehouse?: Warehouse;
  warehouseReference?: string;
  yarnLotItemDetails?: YarnLotItemDetails;
  parentKey?: EntityKeyByNumber;
  currentLocation?: LocationPath;
  movements: MovementLotItem[];
  currentLocationCode: string;
  currentLocationPath: LocationPath;
}

export interface LotItemSearch {
  lotItemNumber?: string;
  manufacturerLabelNumber?: string;
  initiator?: EntityKeyByReference;
  manufacturerReference?: string;
  location?: LocationPath;
}

export interface LotItemInsert {
  providerLotId: string;
  netQuantity: number;
  grossQuantity: number;
  itemCode: string;
  manufacturerReference: string;
  currentLocationCode: string;
  isActive: boolean;
  packagingType: number;
  notes?: string;
}

// ── Supporting types (stubs for types not in Production.UI) ──

export interface Invoice {
  id: string;
  invoiceNumber: string;
  status: number;
  fieldToDisplay?: string;
}

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  fieldToDisplay?: string;
}

export interface Provider {
  id: string;
  name: string;
  fieldToDisplay: string;
}

export interface Customer {
  name: string;
  id: string;
}

export interface EntityKeyByNumber {
  number: string;
  entityDefinitionCode: number;
}

export interface ConsumptionEntry {
  quantity: number;
  initiator: EntityKeyByReference;
}

export interface YearWeek {
  year: number;
  week: number;
}

export interface YarnLotItemDetails {
  coneColor: string;
  coneCount: number;
}

export interface RequisitionNote {
  id?: string;
  richText: string;
  inResponseToReference?: string;
  userLite: UserLite;
  dateModified?: Date;
}

export interface MovementLotItem {
  fromLocationCode: string;
  toLocationCode: string;
  comment?: string;
  movedBy: UserLite;
  movementDate: Date;
}

export interface SplitLotItem {
  id?: string;
  splitQuantity?: number;
  originalGrossQuantity: number;
  originalNetQuantity: number;
  packagingType: number;
}
